import { useNavigate } from 'react-router-dom'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import useUserProfilePoster from '../hooks/useUserProfilePoster'
import { UserProfileDto } from '../types/UserProfileDto'
import { UserProfileJob, UserProfileRole } from '../types/UserProfile'
import { UserGender } from '../types/User'

import UserData from '@/interfaces/UserData'
import { DisplayValueFormat } from '@/components/DisplayValue'
import { Rule, validateFields } from '@/utils/validateForm'
import { SelectField } from '@/components/SelectField'
import CheckboxField from '@/components/CheckBoxField'
import { ButtonAction } from '@/components/Buttons'
import InputField from '@/components/InputField'
import { formatDate } from '@/utils/utils'
import { useAuth } from '@/hooks/useAuth'
import { AppPath } from '@/enums/AppPath'

const UserEdit: React.FC<{ userProfile: UserProfileDto; creating: boolean }> = ({ userProfile, creating }) => {
  const [roles, setRoles] = useState<UserProfileRole[]>(userProfile.profile.roles)
  const { postUserProfile, errorPostUserProfile } = useUserProfilePoster()
  const [formData, setFormData] = useState<UserProfileDto>(userProfile)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [disabled, setDisabled] = useState<boolean>(true)
  const { userData, setUserData, hasRole } = useAuth()
  const { t, i18n } = useTranslation()

  const sameUser = userData?.profile?._id === userProfile.profile._id
  const activated = !!userProfile.user.activatedAt
  const allowModUserData = sameUser || !activated

  // Sin "Gestionar miembros" sólo se editan los datos personales del propio perfil.
  // "Editar Perfil" del menú de arriba trae acá a cualquier usuario. Cargo y permisos no se
  // muestran, y el servidor además los ignora si llegan de alguien sin el permiso (profile_edit).
  const canEditProfiles = hasRole(UserProfileRole.EditProfiles)
  const selfService = sameUser && !canEditProfiles && !creating

  const modifyingUserData = allowModUserData && !creating
  const navigate = useNavigate()

  // Crear o modificar
  const onModifyClick = async () => {
    // Si estamos creando o modificando debemos chequear que no meta basura
    if (modifyingUserData || creating) {
      const fields = [
        { name: 'user.firstname', value: formData.user.firstname, rule: Rule.REQUIRED },
        { name: 'user.lastname', value: formData.user.lastname, rule: Rule.REQUIRED },
        { name: 'profile.email', value: formData.profile.email, rule: Rule.REQUIRED },
        { name: 'user.nin', value: formData.user.nin, rule: Rule.REQUIRED },
        { name: 'user.phone', value: formData.user.phone, rule: Rule.REQUIRED },
        { name: 'profile.job', value: formData.profile.job, rule: Rule.REQUIRED },
      ]

      const validationErrors = validateFields(fields)

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors) // Set errors if any

        return
      }
      setErrors({})
    }

    let res

    if (modifyingUserData || !creating) {
      // Si esta modificando la parte de arriba
      res = await postUserProfile(userProfile.profile._id, roles, formData, allowModUserData)
    } else if (creating) {
      formData.profile.organization = userData?.organization?._id ?? ''
      formData.profile.createdBy = userData?.profile?._id ?? ''

      res = await postUserProfile('', roles, formData, false)
    }

    if (res) {
      // Si el usuario siendo modificado es el mismo conectado
      // hay que refrescar sus datos

      if (modifyingUserData && sameUser) {
        if (userData) {
          setUserData(
            (prev) =>
              ({
                ...prev,
                profile: {
                  ...prev?.profile,
                  ...formData.profile,
                  roles,
                },
                user: {
                  ...prev?.user,
                  ...formData.user,
                },
              }) as UserData
          )
        }
      }

      const url = `/${AppPath.Users}/view/${res}`
      navigate(url)
    }
  }
  const onCancelClick = () => {
    goBack()
  }
  const goBack = () => {
    const url = creating ? `/${AppPath.Users}` : `/${AppPath.Users}/view/${userProfile.profile._id}`
    navigate(url)
  }
  // Handle checkbox change
  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target as { value: UserProfileRole; checked: boolean }

    if (!roles) {
      return
    }

    setDisabled(false)
    setRoles((prev) => (checked ? [...prev, value] : prev.filter((role) => role !== value)))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setDisabled(false)

    // Handle nested fields such as 'profile.email'
    const keys = name.split('.')

    if (keys.length > 1) {
      // For nested fields, like 'profile.email'
      // Check if the property is an object
      const firstKey = keys[0] as keyof UserProfileDto
      const secondKey = keys[1]
      // Ensure prevState[firstKey] is an object before spreading
      const nestedObject = formData[firstKey]

      if (typeof nestedObject === 'object' && nestedObject !== null) {
        setFormData({
          ...formData,
          [firstKey]: {
            ...nestedObject, // Safely spread only if it's an object
            [secondKey]: value,
          },
        })
      } else {
        setFormData({
          ...formData,
          [firstKey]: {
            [secondKey]: value,
          },
        })
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      })
    }
  }

  const buttonLabel = creating ? 'Grabar' : 'Grabar cambios'

  if (!canEditProfiles && !selfService) {
    return <div className="ml-4">No tienes permiso para editar este perfil.</div>
  }

  // Solo modificamos los datos personales si se trata del mismo usuario o si NO esta activado
  return (
    <div className="mb-48 lg:mb-4">
      {/** El mb anterior es para darle espacio abajo en pantallas mas" bajas" */}
      <div className="text-2xl font-bold ml-4">
        {userProfile.user.firstname} {userProfile.user.lastname}
      </div>
      <div className="grid grid-cols-1 gap-4 ">
        <div className="grid grid-cols-1 sm:grid-cols-2  mb-2 lg:w-3/4 gap-2">
          <DisplayValueFormat label="Fecha Ingreso" value={formatDate(userProfile.profile?.createdAt)} />
          {modifyingUserData || creating ? (
            <>
              <InputField
                label="Nombre"
                name="user.firstname"
                value={formData?.user.firstname}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="Apellido"
                name="user.lastname"
                value={formData?.user.lastname}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="Email"
                name="profile.email"
                value={formData?.profile.email}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="C.I."
                name="user.nin"
                value={formData?.user?.nin}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="Teléfono"
                name="user.phone"
                value={formData?.user?.phone}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <SelectField
                label="Género"
                name="user.gender"
                value={formData?.user?.gender}
                options={UserGender}
                handleInputChange={handleInputChange}
              />
            </>
          ) : (
            <>
              <DisplayValueFormat label="Email" value={userProfile.profile?.email} />
              <DisplayValueFormat label="C.I." value={userProfile.user?.nin} />
              <DisplayValueFormat label="Teléfono" value={userProfile.user?.phone} />
              <DisplayValueFormat label="Género" value={t(userProfile.user?.gender)} />
            </>
          )}
          {!selfService && (
            <div className="mt-4 col-span-2">
              <SelectField
                label="Cargo"
                name="profile.job"
                value={formData.profile.job}
                options={UserProfileJob}
                handleInputChange={handleInputChange}
                noFirstOption={!creating}
                errors={errors}
              />
            </div>
          )}
        </div>

        {!selfService && (
          <div className="mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {Object.values(UserProfileRole).map((role) => (
                <CheckboxField
                  checked={roles.includes(role) ?? false}
                  handleInputChange={handleCheckChange}
                  label={t(role)}
                  hint={
                    i18n.exists(role.replace('ROLE.', 'ROLE_HINT.'))
                      ? t(role.replace('ROLE.', 'ROLE_HINT.'))
                      : undefined
                  }
                  value={role}
                  name="roles"
                  key={role}
                  disabled={
                    modifyingUserData &&
                    sameUser &&
                    (role === UserProfileRole.EditProfiles || role === UserProfileRole.ViewProfiles)
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-center mt-4 gap-2">
        <ButtonAction label={buttonLabel} handleButtonClick={onModifyClick} disabled={disabled} />
        <ButtonAction label="Cancelar" handleButtonClick={onCancelClick} />
      </div>

      <div>{errorPostUserProfile?.message}</div>
    </div>
  )
}

export default UserEdit
